<template>
  <PageContentHeader :title="userFullName" :description="activityTitle">
    <template #image>
      <UserAvatar :avatar-url="userAvatarImage" />
    </template>

    <div v-if="!isCertifiedLoading">
      <BaseChip v-if="isCertified" theme="new-leaf">
        {{ $t('activity.APPROVED') }}
      </BaseChip>
      <BaseButton
        v-else
        theme="blue"
        :loading="isCertifyingUser"
        :disabled="!canApproveUser"
        @click="emits('approveUser')"
      >
        {{ $t('approvals.APPROVE_USER') }}
      </BaseButton>
    </div>
    <BlockSkeleton v-else size="large" width="150px" height="50px" />

    <RadialProgressBar
      v-if="!isActivityUserLoading"
      :progress="{ value: itemCount, max: doneCount }"
      progress-type="fraction"
      color="var(--color-green-500)"
      :label="$t('COMPLETED')"
    >
      <template #icon>
        <CheckmarkIcon :style="{ color: 'var(--color-green-500)' }" />
      </template>
    </RadialProgressBar>
    <BlockSkeleton v-else size="large" width="131px" height="50px" />
  </PageContentHeader>
</template>

<script lang="ts" setup>
import BaseButton from '@/components/generics/base/BaseButton.vue'
import BaseChip from '@/components/generics/base/BaseChip.vue'
import RadialProgressBar from '@/components/generics/RadialProgressBar.vue'
import CheckmarkIcon from '@/components/icon_components/CheckmarkIcon.vue'
import PageContentHeader from '@/components/layout/PageContentHeader.vue'
import BlockSkeleton from '@/components/skeletons/BlockSkeleton.vue'
import UserAvatar from '@/components/user/edit/UserAvatar.vue'

import { useContentLocale } from '@/hooks/useContentLocale'
import { useSelfCertification } from '@/hooks/users/useSelfCertification'
import { computed, withDefaults } from 'vue'

interface Props {
  userId?: string | number
  userAvatarImage: string
  userFirstName?: string
  userLastName?: string
  activityTitles?: Record<string, string>
  itemCount: number
  doneCount: number
  isActivityUserLoading?: boolean
  isCertified?: boolean
  isCertifiedLoading?: boolean
  isCertifyingUser?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  userId: undefined,
  userAvatarImage: '',
  userFirstName: '',
  userLastName: '',
  activityTitles: undefined,
  itemCount: 0,
  doneCount: 0,
  isActivityUserLoading: false,
  isCertified: false,
  isCertifiedLoading: false,
  isCertifyingUser: false,
})

const emits = defineEmits<{
  (e: 'approveUser'): void
}>()

const { pickTextFromLocale } = useContentLocale('-')
const { canCertifyUser } = useSelfCertification()

const activityTitle = computed(() => {
  if (!props.activityTitles) return '-'
  return pickTextFromLocale(props.activityTitles)
})

const userFullName = computed(() => {
  return `${props.userFirstName || ''} ${props.userLastName || ''}`
})

const canApproveUser = computed(() => {
  const hasCompletedAllItems =
    props.itemCount === props.doneCount && props.itemCount > 0
  if (!hasCompletedAllItems) {
    return false
  }
  return !props.isCertified && canCertifyUser(props.userId)
})
</script>
